import React, { useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { getAdminAccountStats } from "../../../utils/Api";

interface AccountStats {
  total_users: number;
  free_accounts: number;
  organization_accounts: number;
}

const COLORS = ["#8b5cf6", "#f97316"];

const AccountDistributionChart: React.FC = () => {
  const [stats, setStats] = useState<AccountStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await getAdminAccountStats();
        setStats(response.data);
      } catch (err) {
        setError("Failed to load statistics");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const chartData = stats
    ? [
        { name: "Free", value: stats.free_accounts },
        { name: "Organisation", value: stats.organization_accounts },
      ]
    : [];

  const total = stats?.total_users ?? 0;

  return (
    <div className="bg-white rounded shadow p-4 sm:p-6 h-full">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">
          Account Distribution
        </h2>
        <p className="text-xs sm:text-sm text-gray-500">
          Free vs Organisation accounts
        </p>
      </div>

      {loading ? (
        <div className="h-64 sm:h-80 flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      ) : error ? (
        <div className="h-64 sm:h-80 flex items-center justify-center">
          <div className="text-red-500">{error}</div>
        </div>
      ) : total === 0 ? (
        <div className="h-64 sm:h-80 flex items-center justify-center">
          <div className="text-gray-500">No data available</div>
        </div>
      ) : (
        <div className="h-64 sm:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) =>
                  `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                }
                labelLine={false}
              >
                {chartData.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index] ?? "#gray"} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
                formatter={(value) => [
                  typeof value === "number" ? value.toLocaleString() : value,
                  "Accounts",
                ]}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => {
                  const item = chartData.find((d) => d.name === value);
                  return `${value}: ${item?.value.toLocaleString() ?? 0}`;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default AccountDistributionChart;
